"use client";

import { Box, Button } from "@repo/ui";
import clsx from "clsx";
import { ChevronsDown } from "lucide-react";
import React, { useState } from "react";

interface LoadMoreProps<T> {
  items: T[];
  render: (item: T, index: number) => React.ReactNode;
  initialVisibleItems?: number;
  buttonText?: string;
  className?: string;
  overlayHidden?: boolean;
  wrapper?: React.ReactElement<
    Record<string, unknown>,
    string | React.JSXElementConstructor<any>
  >;
}

const LoadMore = <T,>({
  items,
  render,
  initialVisibleItems = 6,
  buttonText,
  className,
  wrapper,
  overlayHidden = false,
}: LoadMoreProps<T>): React.ReactElement => {
  const [expanded, setExpanded] = useState(false);

  const itemsToShow = expanded ? items : items.slice(0, initialVisibleItems);

  return (
    <div className="relative">
      <Box
        className={clsx(className, {
          "overflow-hidden": !expanded && items.length > initialVisibleItems,
          "pb-4": overlayHidden,
        })}
        render={wrapper ?? <div />}
      >
        {itemsToShow.map((item, index) => render(item, index))}
      </Box>
      {!overlayHidden && (
        <div
          className={clsx(
            "absolute bottom-10 left-0 right-0 h-16 bg-gradient-to-t from-common-white to-transparent transition-opacity duration-500",
            {
              "opacity-0 pointer-events-none":
                expanded || items.length <= initialVisibleItems,
              "opacity-100 pointer-events-auto":
                !expanded && items.length > initialVisibleItems,
            }
          )}
        />
      )}

      {!expanded && items.length > initialVisibleItems && (
        <div className="text-center bg-common-white">
          <Button
            color="primary"
            variant="text"
            onClick={() => setExpanded(true)}
            endIcon={<ChevronsDown size={20} />}
          >
            {buttonText || "View More"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default LoadMore;
